/**
 * Which `<dp>` paths belong on the kernel origin, and which registry row never mounts in the shell
 * realm (kernel apps, WS-16a, design section 3.2, locked decisions 2, 8, 10 and 12, R-016).
 *
 * The boot asks {@link kernelDestinationOf} on every navigation while the kernel origin is on.
 * `AppHost` asks {@link isKernelOnlyEntry} on the matched row.
 */
import { ADMIN_APP_ID, ADMIN_APP_MOUNT_PATH } from '@zion/sdk-dp-admin-contracts';
import { matchSystemRoute, resolvePostLoginPath, type SystemSurfaceName } from '@zion/shell-system-surfaces/protocol';
import type { AppRegistryEntry } from '../api/types.js';
import { isUnderMount } from '../logic/app-loader-utils.js';
import { kernelUrl, type KernelOrigin } from './kernel-origin.js';

/**
 * What a forwarded system path takes to the kernel origin.
 *
 * - `path`: the path alone.
 * - `path-and-query`: the path and the whole query.
 * - `path-and-return-to`: the path, and one `?returnTo=` only when the `returnTo` value names a
 *   `<dp>` path that {@link kernelDestinationOf} sends to the kernel origin as a `leave`. The value
 *   becomes the kernel path and query of that `leave`. Any other value, a `<dp>` app path such as
 *   `/apps/notes` included, goes nowhere, so the kernel page lands on its own default.
 */
export type KernelForward = 'path' | 'path-and-query' | 'path-and-return-to';

/** One system surface that runs only on the kernel origin: its kernel path and what goes with it. */
export interface KernelSystemRoute {
  readonly kernelPath: string;
  readonly forward: KernelForward;
}

/**
 * The seven system surfaces that run only on the kernel origin, by surface name.
 *
 * - `recovery-login` to `/login/recovery`, path: a recovery code mints an owner session (I-8).
 * - `oauth-callback` to `/login`, path: an IdP code never crosses origins, and the flow starts
 *   again on the kernel origin (R-015).
 * - `oauth-consent` to `/oauth/consent`, path and query: the consent approves a delegated token
 *   (R-016).
 * - `password-reset-request` to `/password-reset`, path: the reset ends in a session.
 * - `password-reset-confirm` to `/password-reset/confirm`, path and query: the token arrives from
 *   an email link (OQ-10).
 * - `accept-invitation` to `/invitations/accept`, path and query: the token arrives from an email
 *   link (OQ-10).
 * - `change-password` to `/change-password`, path and return-to: R12, tier 1. The `returnTo` goes
 *   only when it names a path that the kernel origin serves (#1172 R-003).
 *
 * `login` is absent, because it starts a handoff. `logout`, `verify-email`, `not-found`,
 * `maintenance` and `setup` stay on `<dp>`.
 */
export const KERNEL_SYSTEM_ROUTES: ReadonlyMap<SystemSurfaceName, KernelSystemRoute> = new Map<SystemSurfaceName, KernelSystemRoute>([
  ['recovery-login', Object.freeze({ kernelPath: '/login/recovery', forward: 'path' })],
  ['oauth-callback', Object.freeze({ kernelPath: '/login', forward: 'path' })],
  ['oauth-consent', Object.freeze({ kernelPath: '/oauth/consent', forward: 'path-and-query' })],
  ['password-reset-request', Object.freeze({ kernelPath: '/password-reset', forward: 'path' })],
  ['password-reset-confirm', Object.freeze({ kernelPath: '/password-reset/confirm', forward: 'path-and-query' })],
  ['accept-invitation', Object.freeze({ kernelPath: '/invitations/accept', forward: 'path-and-query' })],
  ['change-password', Object.freeze({ kernelPath: '/change-password', forward: 'path-and-return-to' })],
]);

/** The passkeys page of the user's own settings. With the switch on it lives on the kernel origin. */
export const KERNEL_PASSKEYS_PATH = '/settings/passkeys' as const;

/**
 * Where a `<dp>` path goes while the kernel origin is on.
 *
 * - `leave`: navigate the document to `url`, an absolute URL on the kernel origin.
 * - `sign-in`: start a kernel sign-in that brings the user back to `returnTo`, a same-origin path.
 */
export type KernelDestination =
  | { readonly kind: 'leave'; readonly url: string }
  | { readonly kind: 'sign-in'; readonly returnTo: string };

/** The lower-case path without one trailing slash, as the router compares it. */
function routerPath(pathname: string): string {
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed.toLowerCase();
}

/**
 * Where `pathname` and `search` go on the kernel origin `origin`, or `null` when the path stays on
 * `<dp>`.
 *
 * Inputs: `location.pathname` (it starts with `/`) and `location.search` (empty or `?...`). The rules,
 * in order:
 *
 * 1. A path under `ADMIN_APP_MOUNT_PATH`, with `isUnderMount` (case folded, as the router):
 *    `leave` to the same path and query on the kernel origin.
 * 2. {@link KERNEL_PASSKEYS_PATH}, case folded, one trailing slash removed: `leave` to the kernel
 *    passkeys page with no query.
 * 3. The `login` system route: `sign-in`, with `returnTo` from `resolvePostLoginPath` over the
 *    `returnTo` query value. An absent or unsafe value gives `/home`.
 * 4. A system route of {@link KERNEL_SYSTEM_ROUTES}: `leave` to its kernel path. A
 *    `path-and-query` row takes the whole query. A `path-and-return-to` row takes one `?returnTo=`
 *    by the rule of {@link KernelForward}. A `path` row takes no query.
 * 5. Any other path: `null`.
 *
 * It reads `document.baseURI` through `resolvePostLoginPath`, and a `path-and-return-to` row also
 * reads it directly to parse the `returnTo` value. It has no other side effect. It never throws for
 * a `location` input.
 */
export function kernelDestinationOf(origin: KernelOrigin, pathname: string, search: string): KernelDestination | null {
  if (isUnderMount(pathname, ADMIN_APP_MOUNT_PATH)) return { kind: 'leave', url: kernelUrl(origin, pathname, search) };
  if (routerPath(pathname) === KERNEL_PASSKEYS_PATH) return { kind: 'leave', url: kernelUrl(origin, KERNEL_PASSKEYS_PATH) };
  const system = matchSystemRoute(pathname);
  if (system === 'login') {
    return { kind: 'sign-in', returnTo: resolvePostLoginPath(new URLSearchParams(search).get('returnTo')) };
  }
  const route = system === null ? undefined : KERNEL_SYSTEM_ROUTES.get(system);
  if (route === undefined) return null;
  return { kind: 'leave', url: kernelUrl(origin, route.kernelPath, forwardedSearch(origin, route.forward, search)) };
}

/** The query that a system route of `forward` takes to the kernel origin, from the `<dp>` `search`. */
function forwardedSearch(origin: KernelOrigin, forward: KernelForward, search: string): string {
  if (forward === 'path-and-query') return search;
  if (forward === 'path') return '';
  return kernelReturnToSearch(origin, search);
}

/**
 * The `?returnTo=` query for a `path-and-return-to` row, or the empty string.
 *
 * The `returnTo` value of `search` passes `resolvePostLoginPath` first (absent or unsafe gives
 * `/home`). A value whose path is the `change-password` surface answers the empty string, so the
 * rule never runs on itself. Otherwise the value is the `returnTo` only when
 * {@link kernelDestinationOf} answers `leave` for it, and then the answer carries the kernel path
 * and query of that `leave`. Two kinds of value answer the empty string:
 *
 * - a `<dp>` path that stays on `<dp>` (`/apps/notes`, `/home`), because the kernel origin does not
 *   serve it;
 * - the `login` route, because {@link kernelDestinationOf} answers `sign-in` for it, not `leave`.
 */
function kernelReturnToSearch(origin: KernelOrigin, search: string): string {
  const target = new URL(resolvePostLoginPath(new URLSearchParams(search).get('returnTo')), document.baseURI);
  if (matchSystemRoute(target.pathname) === 'change-password') return '';
  const destination = kernelDestinationOf(origin, target.pathname, target.search);
  if (destination?.kind !== 'leave') return '';
  const kernelTarget = new URL(destination.url);
  return `?returnTo=${encodeURIComponent(`${kernelTarget.pathname}${kernelTarget.search}`)}`;
}

/**
 * True when `entry` is a row that never mounts in the shell realm while the kernel origin is on:
 * the admin app by `ADMIN_APP_ID`, or any row whose `mountPath` sits under `ADMIN_APP_MOUNT_PATH`.
 * Pure.
 */
export function isKernelOnlyEntry(entry: AppRegistryEntry): boolean {
  return entry.appId === ADMIN_APP_ID || isUnderMount(entry.manifest.mountPath, ADMIN_APP_MOUNT_PATH);
}
